import { supabase } from '@/integrations/supabase/client';

export interface TrainingGoalProgress {
  goal_id: string;
  current_value: number;
  target_value: number;
  percentage: number;
  goal_type: 'monthly' | 'weekly' | 'custom';
}

/**
 * Check training goal progress and create notifications if milestones are reached
 * This function is called after a training is marked as completed
 */
export const checkTrainingGoalProgress = async (
  userId: string, 
  t?: (key: string, params?: Record<string, string | number>) => string
): Promise<void> => {
  try {
    // Get all training goals for this user
    const { data: goals, error: goalsError } = await supabase
      .from('training_goals')
      .select('id, monthly_goal, weekly_goal, start_date')
      .eq('user_id', userId);

    if (goalsError) {
      console.error('❌ Error fetching training goals:', goalsError);
      return;
    }

    if (!goals || goals.length === 0) {
      return; // No goals to check
    }

    // Process each goal
    for (const goal of goals) {
      // Calculate current completed trainings for this goal's month
      const { data: trainings, error: trainingsError } = await supabase
        .from('trainings')
        .select('id, end_time, status')
        .eq('user_id', userId)
        .eq('status', 'Completed')
        .gte('end_time', new Date(goal.start_date).toISOString().split('T')[0])
        .lt('end_time', new Date(new Date(goal.start_date).getFullYear(), new Date(goal.start_date).getMonth() + 1, 0).toISOString().split('T')[0]);



      if (trainingsError) {
        console.error('❌ Error fetching trainings:', trainingsError);
        continue;
      }
      
      const currentValue = trainings?.length || 0;
      const targetValue = goal.monthly_goal || goal.weekly_goal || 0;
      
      if (targetValue === 0) {
        continue;
      }

      const percentage = Math.round((currentValue / targetValue) * 100);
      const goalType = goal.monthly_goal > 0 ? 'monthly' : 'weekly';
      

      
      // Check if we should create notifications
      await checkAndCreateNotification(userId, goal.id, percentage, currentValue, targetValue, goalType, t);
    }
  } catch (error) {
    console.error('Error checking training goal progress:', error);
  }
};

/**
 * Check if notification should be created and create it if needed
 */
const checkAndCreateNotification = async (
  userId: string,
  goalId: string,
  percentage: number,
  currentValue: number,
  targetValue: number,
  goalType: string,
  t?: (key: string, params?: Record<string, string | number>) => string
): Promise<void> => {
  try {
    // Check for 70% milestone
    if (percentage >= 70 && percentage < 80) {
      const notificationExists = await checkNotificationExists(userId, goalId, '70');
      if (!notificationExists) {
        await createGoalNotification(userId, goalId, percentage, currentValue, targetValue, goalType, '70', t);
      }
    }

    // Check for 80% milestone
    if (percentage >= 80 && percentage < 100) {
      const notificationExists = await checkNotificationExists(userId, goalId, '80');
      if (!notificationExists) {
        await createGoalNotification(userId, goalId, percentage, currentValue, targetValue, goalType, '80', t);
      }
    }

    // Check for 100% milestone (goal completed!)
    if (percentage >= 100) {
      const notificationExists = await checkNotificationExists(userId, goalId, '100');
      if (!notificationExists) {
        await createGoalNotification(userId, goalId, percentage, currentValue, targetValue, goalType, '100', t);
      }
    }
  } catch (error) {
    console.error('❌ Error creating goal notification:', error);
  }
};

/**
 * Check if a notification for a specific milestone already exists
 */
const checkNotificationExists = async (
  userId: string,
  goalId: string,
  milestone: string
): Promise<boolean> => {

  
  const { data, error } = await supabase
    .from('notifications')
    .select('id')
    .eq('user_id', userId)
    .eq('type', 'goal')
    .eq('metadata->goal_id', goalId)
    .eq('metadata->milestone', milestone) // Use milestone instead of percentage
    .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Last 24 hours
    .limit(1);

  if (error) {
    console.error('❌ Error checking notification existence:', error);
    return false;
  }

  const exists = (data?.length || 0) > 0;
  
  return exists;
};

/**
 * Create a goal progress notification
 */
const createGoalNotification = async (
  userId: string,
  goalId: string,
  percentage: number,
  currentValue: number,
  targetValue: number,
  goalType: string,
  milestone: string,
  t?: (key: string, params?: Record<string, string | number>) => string
): Promise<void> => {

  
  let title: string;
  let content: string;
  
  if (t) {
    // Use translations if available
    try {
      title = t('notifications.training_goals.title', {});
      
      if (milestone === '70') {
        content = t('notifications.training_goals.70_percent', { percentage });
      } else if (milestone === '80') {
        content = t('notifications.training_goals.80_percent', { percentage });
      } else {
        content = t('notifications.training_goals.100_percent', {});
      }
    } catch (error) {
      // Fallback to hardcoded Portuguese if translation fails
      title = 'Training Goal Progress';
      if (milestone === '70') {
        content = `Você atingiu ${percentage}% da sua meta de treinos! Falta pouco para a vitória. Oss!`;
      } else if (milestone === '80') {
        content = `Você atingiu ${percentage}% da sua meta de treinos! Quase lá! Oss!`;
      } else {
        content = `🎉 PARABÉNS! Você completou 100% da sua meta de treinos! Meta atingida! Oss! 🎉`;
      }
    }
  } else {
    // Fallback to hardcoded Portuguese
    title = 'Training Goal Progress';
    if (milestone === '70') {
      content = `Você atingiu ${percentage}% da sua meta de treinos! Falta pouco para a vitória. Oss!`;
    } else if (milestone === '80') {
      content = `Você atingiu ${percentage}% da sua meta de treinos! Quase lá! Oss!`;
    } else {
      content = `🎉 PARABÉNS! Você completou 100% da sua meta de treinos! Meta atingida! Oss! 🎉`;
    }
  }



  const { error } = await supabase
    .from('notifications')
    .insert({
      user_id: userId,
      type: 'goal',
      title,
      content,
      metadata: {
        goal_id: goalId,
        percentage,
        current_value: currentValue,
        target_value: targetValue,
        goal_type: goalType,
        milestone
      },
      action_url: '/home',
      target_id: goalId,
      target_type: 'training_goal'
    });

    if (error) {
    console.error('❌ Error creating goal notification:', error);
  }
};
